import { Kit, SportCompetitor } from "@fresh8/types/src/betData";
import { Sport, SportEnum } from "@fresh8/types/src/language";
import { deltaE } from "color-delta-e";
import { BrandContext, injectBrand } from "../brand";
import { awayFirstSports } from "./orderedCompetitorData";

export interface TeamColor {
  banner: {
    background: string;
    text: string;
  };
  helmets: {
    accent: string;
    background: string;
  };
  jersey: {
    background: string;
    base?: string;
    number?: string;
    outline?: string;
    sleeve?: string;
    shirtType?: string;
    squares?: string;
    horizontalStripe?: string;
    verticalStripe?: string;
    split?: string;
    chest?: string;
    neck?: string;
  };
}

export interface TeamColors {
  home: TeamColor | undefined;
  away: TeamColor | undefined;
}

export function getColorsForCompetitors(
  competitors: SportCompetitor[],
  background: string,
  sport: Sport
): TeamColors {
  if (!competitors[0].colors || !competitors[1].colors) {
    return {
      home: undefined,
      away: undefined
    };
  }

  return colorSelector(competitors, background, sport);
}

type ValidColor = string | Kit;

function isString(possibleString: unknown): possibleString is string {
  return typeof possibleString === "string";
}

function getColor(
  compares: { backgroundColor?: string; teamCompare?: string },
  arr: Array<ValidColor | undefined>,
  thresholdOverrides?: Partial<{ bg: number; team: number }>
): ValidColor | undefined {
  let primary: ValidColor | undefined = undefined;

  const thresholds = {
    bg: 15,
    team: 35,
    ...thresholdOverrides
  };

  for (const color of arr) {
    let current = color;

    if (current === undefined) continue;

    if (!isString(current)) {
      if (current?.base === undefined) continue;
      current = current.base;
    }

    if (compares.backgroundColor) {
      const bgDif = Math.floor(deltaE(compares.backgroundColor, current));

      // if color and background are too similar skip to next color
      if (bgDif < thresholds.bg) continue;
    }

    // if were also comparing to another team compare those colors
    if (compares?.teamCompare) {
      const teamDif = Math.floor(deltaE(compares.teamCompare, current));
      // if other team clashes with current skip to next color
      if (teamDif < thresholds.team) continue;
    }

    /**
     * first color we find that doesnt clash with background and other team (if thats available to compare)
     * then select that color  and break out of the loop
     */

    primary = color;
    break;
  }

  return primary;
}

function getBannerColor(
  compares: { backgroundColor: string; teamCompare?: string },
  arr: Array<ValidColor | undefined>
): TeamColor["banner"] | undefined {
  let color = getColor(compares, arr, { team: 22 });

  /**
   * this will only be undefined if all colors either clash or aren't defined, we return undefined
   * so components can default to their  branding colors
   */
  if (color === undefined) return undefined;

  color = isString(color) ? color : color.base;

  return {
    background: color,
    text: textContrast(color)
  };
}

const toCamelCase = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
};

function getJerseyColor(
  compares: {
    teamCompare?: string;
  },
  arr: Array<Kit | undefined>
): TeamColor["jersey"] {
  // doesnt care about team clash
  // home team will select Home -> Thirds -> Home order
  // away team will select Away -> Thirds -> Home order

  const color = getColor(compares, arr) as Kit | undefined;

  if (color === undefined) return {} as TeamColor["jersey"];

  return Object.entries({ ...color, background: color.base }).reduce(
    (acc, [key, value]) => {
      acc[toCamelCase(key)] = value;
      return acc;
    },
    {} as TeamColor["jersey"]
  );
}

function getHeadwearColor(
  main: { primary?: string; secondary?: string },
  fallbacks: Array<ValidColor | undefined>
): TeamColor["helmets"] {
  if (main.primary && main.secondary) {
    return { background: main.primary, accent: main.secondary };
  }

  let color = getColor({}, fallbacks);

  if (color === undefined) return {} as TeamColor["helmets"];

  const colorIndex = fallbacks
    .filter(v => v !== undefined)
    .findIndex(v => v === (color as any));

  let accent =
    colorIndex < fallbacks.length - 1 ? fallbacks[colorIndex + 1] : "#fff";
  color = isString(color) ? color : color.base;
  accent = isString(accent) ? accent : accent?.base || "#fff";

  return {
    background: color,
    accent: accent
  };
}

function textContrast(color: string): string {
  return deltaE(color, "#fff") < 40 ? "#000" : "#fff";
}

const cache = new Map<string, TeamColors>();

export function colorSelector(
  competitors: SportCompetitor[],
  background: string,
  sport: Sport
): TeamColors {
  const brandContext = injectBrand() || ({} as BrandContext);
  const awayFirst = awayFirstSports(sport);
  const result = cache.get(JSON.stringify({ competitors, background }));

  if (result) return result;

  const [homeTeamColors, awayTeamColors] = awayFirst
    ? [competitors[1].colors, competitors[0].colors]
    : [competitors[0].colors, competitors[1].colors];

  const useJerseys = [
    SportEnum.BASKETBALL,
    SportEnum.ICE_HOCKEY,
    SportEnum.FOOTBALL
  ].includes(sport);

  let homeTeamBanner = getBannerColor({ backgroundColor: background }, [
    homeTeamColors?.primary,
    homeTeamColors?.secondary,
    homeTeamColors?.home,
    homeTeamColors?.away,
    homeTeamColors?.third
  ]);

  let awayTeamBanner = getBannerColor(
    { backgroundColor: background, teamCompare: homeTeamBanner?.background },
    [
      awayTeamColors?.primary,
      awayTeamColors?.secondary,
      awayTeamColors.home,
      awayTeamColors.away,
      awayTeamColors.third
    ]
  );

  if (homeTeamBanner === undefined || awayTeamBanner === undefined) {
    homeTeamBanner = {
      background: brandContext.getColorStyle(
        brandContext.adTemplate.value?.team.banner.backgroundColor
      ),
      text: brandContext.getFontStyle(
        brandContext.adTemplate.value?.team.banner.font
      ).color
    };
    awayTeamBanner = {
      background: brandContext.getColorStyle(
        brandContext.adTemplate.value?.team.banner.backgroundColor
      ),
      text: brandContext.getFontStyle(
        brandContext.adTemplate.value?.team.banner.font
      ).color
    };
  }

  const HomeJerseyOrder = awayFirst
    ? [homeTeamColors.home, homeTeamColors.away, homeTeamColors.third]
    : [homeTeamColors.home, homeTeamColors.away, homeTeamColors.third];

  const AwayJerseyOrder = awayFirst
    ? [awayTeamColors.away, awayTeamColors.third, awayTeamColors.home]
    : [awayTeamColors.home, awayTeamColors.away, awayTeamColors.third];

  const homeJerseys = useJerseys
    ? getJerseyColor({}, HomeJerseyOrder)
    : ({} as TeamColor["jersey"]);
  const awayJerseys = useJerseys
    ? getJerseyColor({ teamCompare: homeJerseys?.background }, AwayJerseyOrder)
    : ({} as TeamColor["jersey"]);

  const homeHelmets = !useJerseys
    ? getHeadwearColor(
        {
          primary: homeTeamColors.primary,
          secondary: homeTeamColors.secondary
        },
        [homeTeamColors.home, homeTeamColors.away, homeTeamColors.third]
      )
    : ({} as TeamColor["helmets"]);

  const awayHelmets = !useJerseys
    ? getHeadwearColor(
        {
          primary: awayTeamColors?.primary,
          secondary: awayTeamColors.secondary
        },
        [awayTeamColors.home, awayTeamColors.away, awayTeamColors.third]
      )
    : ({} as TeamColor["helmets"]);

  const teamColors = {
    home: awayFirst
      ? {
          banner: awayTeamBanner,
          helmets: awayHelmets,
          jersey: awayJerseys
        }
      : {
          banner: homeTeamBanner,
          helmets: homeHelmets,
          jersey: homeJerseys
        },
    away: awayFirst
      ? {
          banner: homeTeamBanner,
          helmets: homeHelmets,
          jersey: homeJerseys
        }
      : {
          banner: awayTeamBanner,
          helmets: awayHelmets,
          jersey: awayJerseys
        }
  };

  cache.set(JSON.stringify({ competitors, background }), teamColors);

  return teamColors;
}
